'use client';

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ROLES } from 'types';

import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { CheckboxWithLink } from '@components/TermsCheckbox';
import { showToast } from '@components/ToastContainer';

import { validateSubstituteName } from '@utils/helpers/contactValidation';

interface ViewSchoolSectionProps {
  substituteName: string;
  onSubstituteNameChange: (name: string) => void;
  bringSubstitute?: boolean;
  onBringSubstituteChange?: (checked: boolean) => void;
  listingId?: string;
  triggerValidation?: boolean;
}

/**
 * Displays a button to view the school, a checkbox to bring somebody and an input to fill in the substitute name.
 * @param props.substituteName - The name of the substitute
 * @param props.onSubstituteNameChange - The function to change the substitute name
 * @param props.listingId - The ID of the school listing to view
 */
export const ViewSchoolModalSection: React.FC<ViewSchoolSectionProps> = ({
  substituteName,
  onSubstituteNameChange,
  bringSubstitute = true,
  onBringSubstituteChange,
  listingId,
  triggerValidation,
}) => {
  const { t } = useTranslation('common');
  const [nameError, setNameError] = useState(false);

  // Use local state if no parent handler provided
  const [_localChecked, setLocalChecked] = useState(true);
  const checked = bringSubstitute;
  const showInput = checked;

  // Parent-controlled validation: only validate when parent explicitly triggers it
  // This ensures validation happens at the right time (e.g., on form submit)
  useEffect(() => {
    if (triggerValidation) {
      const isValid = validateSubstituteName(substituteName, checked);
      setNameError(!isValid);
    }
  }, [triggerValidation, checked, substituteName]);

  const handleCheckboxChange = () => {
    const newValue = !checked;
    if (onBringSubstituteChange) {
      onBringSubstituteChange(newValue);
    } else {
      setLocalChecked(newValue);
    }

    // Clear name and error when hiding input
    if (checked) {
      onSubstituteNameChange('');
      setNameError(false);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onSubstituteNameChange(value);

    // Clear error when user starts typing
    if (nameError) {
      setNameError(false);
    }
  };

  const handleViewSchool = () => {
    if (listingId) {
      const targetPath = `/${ROLES.TEACHER}/listing/${listingId}`;
      window.open(targetPath, '_blank', 'noopener,noreferrer');
    } else {
      showToast('error', t('toast.errorSchoolNotAvailable'), t('toast.errorListingNotAvailable'));
    }
  };

  return (
    <>
      <Button label={t('viewSchool')} onClick={handleViewSchool} variant={'secondary'} />
      <CheckboxWithLink
        label={t('teacherShifts.bringSomebody')}
        checked={checked}
        onChange={handleCheckboxChange}
      />
      {showInput && (
        <Input
          label={t('teacherShifts.whoWithYou')}
          type="name"
          placeholder={t('teacherShifts.fillSubstituteName')}
          required={checked}
          id="name"
          value={substituteName}
          onChange={handleNameChange}
          hasError={nameError}
        />
      )}
    </>
  );
};
